import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { IOrganization } from './organization';
import { IOrganizationFormValues } from './organizationFormValues';
import { IOrganizationUpsert } from './organizationUpsert';
import { ITumCredentials } from '../tumCredentials/tumCredentials';
import { IOptionType } from '@zz2/zz2-ui';
import { IContactInfo } from '../contactInfo/contactInfo';
import OptionType from '../../optionType';
import { isNullOrWhiteSpace } from '../../../../services/appFunctionsService';

export default class OrganizationModelHelper {
    public static createFormValues = (
        entry : IOrganization | null,
        contactInfos : Array<IContactInfo>,
        tumCredentialsList : Array<ITumCredentials>
    ) : IOrganizationFormValues => {
        if (!entry) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            name: '',
            isSupplier: false,
            isClient: false,
            canIntake: false,
            requiresProject: false,
            exporterCode: '',
            billingParty: '',
            waybillPrefix: '',
            nextWaybillNumber: null,
            waybillRangeEnd: null,
            tumBusinessId: null,
            tumLogisticalProviderId: null,
            tumCredentials: null,
            contactInfos: [],
            isActive: true,
        };

        const tumCredentialOption = tumCredentialsList.find(x => x.id === entry.tumCredentialsId);

        const contactInfoOptions = contactInfos
            .filter(x => entry.contactInfoIds.includes(x.id))
            .map(x => OptionType.fromDataModel(x));

        return {
            id: entry.id,
            guid: entry.guid,
            code: entry.code,
            name: entry.name,
            isSupplier: entry.isSupplier,
            isClient: entry.isClient,
            canIntake: entry.canIntake,
            requiresProject: entry.requiresProject,
            exporterCode: entry.exporter,
            billingParty: entry.billingParty,
            waybillPrefix: entry.waybillPrefix,
            nextWaybillNumber: entry.nextWaybillNumber,
            waybillRangeEnd: entry.waybillRangeEnd,
            tumBusinessId: entry.tumBusinessId,
            tumLogisticalProviderId: entry.tumLogisticalProviderId,
            tumCredentials: tumCredentialOption
                ? OptionType.fromDataModel(tumCredentialOption)
                : null,
            contactInfos: contactInfoOptions,
            isActive: entry.isActive,
        };
    };

    public static createUpsert = (formValues : IOrganizationFormValues) : IOrganizationUpsert => {
        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            name: formValues.name,
            isSupplier: formValues.isSupplier,
            isClient: formValues.isClient,
            canIntake: formValues.canIntake,
            requiresProject: formValues.requiresProject,
            exporterCode: !isNullOrWhiteSpace(formValues.exporterCode)
                ? formValues.exporterCode
                : null,
            billingParty: !isNullOrWhiteSpace(formValues.billingParty)
                ? formValues.billingParty
                : null,
            waybillPrefix: !isNullOrWhiteSpace(formValues.waybillPrefix)
                ? formValues.waybillPrefix
                : null,
            nextWaybillNumber: formValues.nextWaybillNumber,
            waybillRangeEnd: formValues.waybillRangeEnd,
            tumBusinessId: formValues.tumBusinessId,
            tumLogisticalProviderId: formValues.tumLogisticalProviderId,
            tumCredentialsId: formValues.tumCredentials
                ? Number(formValues.tumCredentials.value)
                : null,
            contactInfoIds: formValues.contactInfos.map(x => Number(x.value)),
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<IOrganizationFormValues> => Yup.object().shape({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        name: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        exporterCode: Yup.string().trim().max(255, 'Exporter can only be up to 255 characters long.').defined().nullable(),
        billingParty: Yup.string().trim().max(255, 'Billing Party can only be up to 255 characters long.').defined().nullable(),
        waybillPrefix: Yup.string().trim().max(1, 'Waybill Prefix can only be 1 character long.').defined().nullable(),
        nextWaybillNumber: Yup.number().positive('Must be a positive number').defined().nullable(),
        waybillRangeEnd: Yup.number().positive('Must be a positive number').defined().nullable(),
        tumBusinessId: Yup.number().positive('Must be a positive number').defined().nullable(),
        tumLogisticalProviderId: Yup.number().positive('Must be a positive number').defined().nullable(),
        tumCredentials: Yup.mixed<IOptionType>().defined().nullable(),
        contactInfos: Yup.array<IOptionType>().defined().required('Required'),
        isSupplier: Yup.boolean().required('Required'),
        isClient: Yup.boolean().required('Required'),
        canIntake: Yup.boolean().required('Required'),
        requiresProject: Yup.boolean().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });
}
